/** leads/proposalShared.tsx — 제안서(A4) 공용 조각: 머리·꼬리(연락처)·섹션 제목·절차 행 (2026-09-19, LeadProposal 에서 분리)
 *  경정청구 제안서(ProposalRefund)와 인증 연장 제안서(ProposalRenewal)가 같은 조판 규칙을 쓴다 — 디자인 핸드오프 2026-09-10 그대로.
 *  작은 글씨 오렌지는 #b83e1c, 강조 오렌지는 #e8552d, 본문 회색 #5c5c5c, 보조 #6c6c6c. */
import React from 'react';
import type { ProposalContact } from './types';

export const fmt = (n: number) => Math.round(n).toLocaleString('ko-KR');
export const ORANGE = '#e8552d';
export const ORANGE_D = '#b83e1c';

/** 각 면 머리 — 조직명 · 부제 | 우측 메타 */
export function Head({ org, sub, right }: { org: string; sub: string; right: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{org}</span><span style={{ fontSize: 12, color: '#6c6c6c' }}>{sub}</span></div>
      <span style={{ fontSize: 12, color: '#6c6c6c' }}>{right}</span>
    </div>
  );
}

/** 각 면 꼬리 — 연락처(담당 FP 첫 항목은 크게) | 우측 면 번호·근거 */
export function Foot({ contacts, right }: { contacts: ProposalContact[]; right: React.ReactNode }) {
  return (
    <div style={{ marginTop: 'auto', borderTop: '1px solid #eceae7', padding: '13px 0 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
      <div style={{ display: 'flex', gap: 22 }}>
        {contacts.map((c, i) => (
          <div key={i} style={{ fontSize: i === 0 ? 12.5 : 11.5, lineHeight: 1.5 }}>{/* 담당 FP(첫 항목)는 이름·연락처를 크게 (사용자 지시 2026-09-10) */}
            <div style={{ color: '#6c6c6c' }}>{c.role}</div>
            <div><b style={{ fontSize: i === 0 ? 16 : 13.5 }}>{c.name}</b> <span style={{ color: '#3c3c3c', fontVariantNumeric: 'tabular-nums', fontSize: i === 0 ? 15 : 12.5, fontWeight: i === 0 ? 600 : 400 }}>{c.phone}</span></div>
          </div>
        ))}
      </div>
      <span style={{ fontSize: 10.5, color: '#6c6c6c', textAlign: 'right' }}>{right}</span>
    </div>
  );
}

/** 섹션 제목 — 좌 굵은 제목 · 우 작은 메타 */
export function SecTitle({ t, meta }: { t: string; meta: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 9 }}>
      <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.02em', whiteSpace: 'nowrap', flexShrink: 0 }}>{t}</span><span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: '#6c6c6c', textAlign: 'right', lineHeight: 1.5 }}>{meta}</span>
    </div>
  );
}

/** 절차 표 한 행 — 번호(원) · 단계명 · 설명 · 담당/기간. hot = 오렌지 강조 */
export function Step({ n, name, desc, who, hot, last }: { n: number; name: string; desc: React.ReactNode; who: string; hot?: boolean; last?: boolean }) {
  return (
    <tr style={{ borderBottom: last ? undefined : '1px solid #f2f0ed', background: hot ? '#fdf1ec' : undefined }}>
      <td style={{ padding: '9px 14px', width: 34 }}><span style={{ width: 24, height: 24, borderRadius: '50%', background: hot ? ORANGE : '#1a1a1a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{n}</span></td>
      <td style={{ padding: '9px 10px', fontWeight: 700, width: 170 }}>{name}</td>
      <td style={{ padding: '9px 10px', color: '#5c5c5c', lineHeight: 1.6 }}>{desc}</td>
      <td style={{ padding: '9px 14px', color: '#6c6c6c', width: 128, textAlign: 'right', whiteSpace: 'nowrap' }}>{who}</td>
    </tr>
  );
}

/** 둥근 테두리 상자 (표·카드 공용) */
export const boxStyle: React.CSSProperties = { border: '1px solid #eceae7', borderRadius: 14, overflow: 'hidden' };
/** 「대표님이 준비하실 것 / 주의할 점」 2단 상자 */
export function TwoBoxes({ a, b }: { a: { title: string; lines: React.ReactNode[] }; b: { title: string; lines: React.ReactNode[] } }) {
  const box = (x: { title: string; lines: React.ReactNode[] }) => (
    <div style={{ border: '1px solid #eceae7', borderRadius: 14, padding: '15px 17px' }}>
      <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 7 }}>{x.title}</div>
      <div style={{ fontSize: 12, color: '#5c5c5c', lineHeight: 1.75 }}>{x.lines.map((l, i) => <React.Fragment key={i}>{['①', '②', '③', '④', '⑤', '⑥'][i]} {l}{i < x.lines.length - 1 ? <br /> : null}</React.Fragment>)}</div>
    </div>
  );
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{box(a)}{box(b)}</div>;
}
